import { useMemo, useState } from 'react';
import { Col, Collapse, Row } from 'react-bootstrap';
import {
  Bar,
  BarChart,
  LabelList,
  ResponsiveContainer,
  Text,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { YAxisTickContentProps } from 'recharts';

import { Button } from '../core/components/buttons';
import Panel from '../core/components/panel';
import KpiCard from '../reports/components/KpiCard';
import {
  APPOINTMENT_SERIES,
  CHART_AXIS_TICK_PROPS,
  CHART_BAR_RADIUS_HORIZONTAL,
  CHART_TOOLTIP_CLASS_NAME,
} from '../reports/components/chartTheme';
import { formatCount, formatPercent } from '../reports/components/formatters';
import { PANEL_FLEX_CLASS_NAME, PANEL_HEADER_CLASS_NAME } from '../reports/components/sovkombank/panelKit';

import {
  buildActivityKpi,
  buildDeviceDimension,
  buildEventTypeDimension,
  buildTopUriDimension,
  DimensionPoint,
} from './activityAggregations';
import type { ClientActivity, ClientActivityEvent } from './store/activityStore';

interface DimensionTooltipProps {
  active?: boolean,
  payload?: { payload: DimensionPoint }[],
  unitLabel: string,
}

const DimensionTooltip = ({ active, payload, unitLabel }: DimensionTooltipProps) => {
  if (!active || !payload?.length) {
    return null;
  }

  const point = payload[0].payload;

  return (
    <div className={ CHART_TOOLTIP_CLASS_NAME }>
      <div className='font-weight-bold'>{ point.label }</div>
      <div>{ unitLabel }: { formatCount(point.count) } ({ formatPercent(point.share) })</div>
    </div>
  );
};

// Тик оси разреза в одну строку: длинные подписи (URI, домены) обрезаются
// с «…» по ширине оси, полное значение — в тултипе (приём DimensionChart).
const renderDimensionTick = (props: YAxisTickContentProps) => (
  <Text { ...props } { ...CHART_AXIS_TICK_PROPS } maxLines={ 1 }>{ props.payload?.value }</Text>
);

interface ActivityDimensionChartProps {
  data: DimensionPoint[],
  labelWidth: number,
  unitLabel: string,
}

// Топ сегментов горизонтальными барами — мини-версия DimensionChart отчётов
// (без кликов и кросс-фильтров: на вкладке они пока не нужны).
const ActivityDimensionChart = ({ data, labelWidth, unitLabel }: ActivityDimensionChartProps) => {
  if (!data.length) {
    return <div className='small text-muted'>Нет данных</div>;
  }

  const chartData = data.map((point) => ({ ...point, countLabel: formatCount(point.count) }));

  return (
    <ResponsiveContainer width='100%' height={ data.length * 30 + 20 }>
      <BarChart
        data={ chartData }
        layout='vertical'
        margin={ { top: 5, right: 40, left: 5, bottom: 5 } }
      >
        <XAxis type='number' hide />
        <YAxis
          type='category'
          dataKey='label'
          width={ labelWidth }
          tick={ renderDimensionTick }
          tickLine={ false }
          axisLine={ false }
        />
        <Tooltip content={ <DimensionTooltip unitLabel={ unitLabel } /> } />
        <Bar
          dataKey='count'
          barSize={ 18 }
          fill={ APPOINTMENT_SERIES.records.color }
          radius={ CHART_BAR_RADIUS_HORIZONTAL }
        >
          <LabelList dataKey='countLabel' position='right' />
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
};

const panelHeader = (title: string, note?: string) => (
  <>
    { title }
    { note && <span className='small font-weight-normal text-muted ml-2'>{ note }</span> }
  </>
);

interface ClientActivityDashboardsProps {
  activity: ClientActivity,
  eventsSample: ClientActivityEvent[],
}

// Краткие дашборды вкладки «Активность клиента» в стиле «Аналитики клиентов»:
// KPI-строка + мини-панели разрезов. Разрезы устройств точные (вес — seenCount
// за всю историю), «События» и «Top-10 URI» — по выборке последних событий.
const ClientActivityDashboards = ({ activity, eventsSample }: ClientActivityDashboardsProps) => {
  const [open, setOpen] = useState(true);

  const kpi = useMemo(() => buildActivityKpi(activity), [activity]);

  const devices = activity?.devices ?? [];
  const devicesNote = 'по всем запросам';

  const platform = useMemo(() => buildDeviceDimension(devices, d => d.platform), [devices]);
  const os = useMemo(() => buildDeviceDimension(devices, d => d.osName), [devices]);
  const browser = useMemo(() => buildDeviceDimension(devices, d => d.browserName), [devices]);
  const deviceModel = useMemo(() => buildDeviceDimension(devices, d => d.deviceModel), [devices]);
  const source = useMemo(() => buildDeviceDimension(devices, d => d.lastSourceDomain), [devices]);

  const eventTypes = useMemo(() => buildEventTypeDimension(eventsSample), [eventsSample]);
  const topUri = useMemo(() => buildTopUriDimension(eventsSample), [eventsSample]);

  // Честная подпись выборки: «все» — если событий не больше размера выборки
  const totalEvents = activity?.total ?? 0;
  const sampleNote = eventsSample.length > 0 && totalEvents > eventsSample.length
    ? `по последним ${ eventsSample.length } событиям`
    : `по всем ${ totalEvents } событиям`;

  const uriDistinctTotal = useMemo(
    () => new Set(eventsSample.map(event => (event?.requestUri ?? '').split('?', 2)[0])).size,
    [eventsSample]
  );

  if (!activity) {
    return null;
  }

  return (
    <div className='mb-4'>
      <Button
        variant='link'
        className='p-0 mb-2 font-weight-bold text-decoration-none'
        icon={ open ? 'chevron-down' : 'chevron-right' }
        text='Сводка по активности'
        onClick={ () => setOpen(!open) }
      />
      <Collapse in={ open }>
        <div>
          <Row className='mb-2'>
            <KpiCard title='Всего событий' value={ formatCount(kpi.totalEvents) } />
            <KpiCard title='Устройств' value={ formatCount(kpi.devicesCount) } />
            <KpiCard title='Запросов' value={ formatCount(kpi.requestsCount) } caption='суммарно по устройствам' />
            <KpiCard title='Первый вход' value={ kpi.firstSeen || '—' } />
            <KpiCard title='Последняя активность' value={ kpi.lastSeen || '—' } />
          </Row>
          <Row>
            <Col lg={ 4 } className='d-flex'>
              <Panel
                className={ PANEL_FLEX_CLASS_NAME }
                headerClassName={ PANEL_HEADER_CLASS_NAME }
                header={ panelHeader('Платформа', devicesNote) }
              >
                <ActivityDimensionChart data={ platform } labelWidth={ 150 } unitLabel='Запросов' />
              </Panel>
            </Col>
            <Col lg={ 4 } className='d-flex'>
              <Panel
                className={ PANEL_FLEX_CLASS_NAME }
                headerClassName={ PANEL_HEADER_CLASS_NAME }
                header={ panelHeader('ОС', devicesNote) }
              >
                <ActivityDimensionChart data={ os } labelWidth={ 150 } unitLabel='Запросов' />
              </Panel>
            </Col>
            <Col lg={ 4 } className='d-flex'>
              <Panel
                className={ PANEL_FLEX_CLASS_NAME }
                headerClassName={ PANEL_HEADER_CLASS_NAME }
                header={ panelHeader('Браузер', devicesNote) }
              >
                <ActivityDimensionChart data={ browser } labelWidth={ 150 } unitLabel='Запросов' />
              </Panel>
            </Col>
            <Col lg={ 4 } className='d-flex'>
              <Panel
                className={ PANEL_FLEX_CLASS_NAME }
                headerClassName={ PANEL_HEADER_CLASS_NAME }
                header={ panelHeader('Устройство', devicesNote) }
              >
                <ActivityDimensionChart data={ deviceModel } labelWidth={ 150 } unitLabel='Запросов' />
              </Panel>
            </Col>
            <Col lg={ 4 } className='d-flex'>
              <Panel
                className={ PANEL_FLEX_CLASS_NAME }
                headerClassName={ PANEL_HEADER_CLASS_NAME }
                header={ panelHeader('Источник', devicesNote) } >
                <ActivityDimensionChart data={ source } labelWidth={ 150 } unitLabel='Запросов' />
              </Panel>
            </Col>
            <Col lg={ 4 } className='d-flex'>
              <Panel
                className={ PANEL_FLEX_CLASS_NAME }
                headerClassName={ PANEL_HEADER_CLASS_NAME }
                header={ panelHeader('События', sampleNote) }
              >
                <ActivityDimensionChart data={ eventTypes } labelWidth={ 150 } unitLabel='Событий' />
              </Panel>
            </Col>
            <Col lg={ 12 } className='d-flex'>
              <Panel
                className={ PANEL_FLEX_CLASS_NAME }
                headerClassName={ PANEL_HEADER_CLASS_NAME }
                header={ panelHeader('Top-10 URI запроса', sampleNote) }
              >
                <ActivityDimensionChart data={ topUri } labelWidth={ 420 } unitLabel='Событий' />
                { (topUri.length > 0 && uriDistinctTotal > topUri.length) && (
                  <div className='small text-muted'>
                    { `Показаны 10 из ${ uriDistinctTotal } URI` }
                  </div>
                ) }
              </Panel>
            </Col>
          </Row>
        </div>
      </Collapse>
    </div>
  );
};

export default ClientActivityDashboards;
